"use client";

import { SimpleColumn } from "@/lib/column-manager";
import {
  BaseEntity,
  SimpleTableConfig,
} from "@/components/shared/UniversalCRMTable/types";
import {
  getCategoryForField,
  getDefaultWidth,
  getMobilePriority,
  getMobileType,
} from "./helpers";
import { createRenderFunction } from "./renderers";

/**
 * Factory function to convert SimpleTableConfig fields to ColumnManager SimpleColumn format
 */
export function createColumnsFromConfig<T extends BaseEntity>(
  config: SimpleTableConfig<T>,
): SimpleColumn[] {
  const columns: SimpleColumn[] = [];

  // Explicit default column order (the uncustomized left-to-right order). Listed
  // fields lead in this exact sequence; everything else follows in definition
  // order (offset by LEAD_ORDER_OFFSET so it always sorts after the lead group).
  const leadOrder = config.tableColumnOrder ?? [];
  const LEAD_ORDER_OFFSET = 1000;

  // Convert each field in the config to a SimpleColumn
  Object.entries(config.fields).forEach(([fieldName, fieldConfig], index) => {
    const leadIndex = leadOrder.indexOf(fieldName);
    const priority = leadIndex >= 0 ? leadIndex : LEAD_ORDER_OFFSET + index;
    const column: SimpleColumn = {
      id: fieldName,
      label: fieldConfig.label,
      category: getCategoryForField(fieldConfig, fieldName),
      visible: fieldConfig.type === "custom" ? true : fieldConfig.hideable !== false, // Custom components visible by default
      width: fieldConfig.width || getDefaultWidth(fieldConfig.type),
      priority,
      render: createRenderFunction(fieldConfig, fieldName),
      mobilePriority: getMobilePriority(fieldName, index),
      mobileType: getMobileType(fieldConfig, fieldName),
      isForeignKey: fieldConfig.isForeignKey,
      foreignKey: fieldConfig.foreignKey,
      isReadOnlyDerived: fieldConfig.isReadOnlyDerived,
      readOnlyReason: fieldConfig.readOnlyReason,
      dataLineage: fieldConfig.dataLineage,
    };

    columns.push(column);
  });

  // Add actions column if needed
  if (config.features?.bulkActions) {
    columns.push({
      id: "actions",
      label: "Actions",
      category: "actions",
      visible: true,
      width: 120,
      priority: 1_000_000,
      mobileType: "hidden", // Hide actions on mobile by default
    });
  }

  return columns.sort((a, b) => a.priority - b.priority);
}
